import { motion } from 'framer-motion'
import React, { useContext, useMemo } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import CardContext from '../../../cardContext'
import { CoverImageDisplayMode, PlaceholderColor } from '../../../types'
import { useWeekViewSettings } from '../../../WeekViewSettingsContext'
import RenamePopover from '../../RenamePopover'
import { lookupPlaceholderHex } from '../lookupPlaceholderHex'
import type { Moment, ContextMenuCallbacks } from '../momentCardTypes'
import { HeadingAndDetails } from './HeadingAndDetails'
import { MomentCardImage } from './MomentCardImage'
import ConnectedMomentWorkflowStatus from './MomentWorkflowStatus/ConnectedMomentWorkflowStatus'

type MomentCardBodyProps = {
  className?: string
  closeRenamePopover?: () => void
  contextMenuCallbacks?: ContextMenuCallbacks
  isRenamePopoverOpen?: boolean
  isSelectable?: boolean
  isSelected?: boolean
  moment: Moment
  momentCardRef: React.MutableRefObject<HTMLDivElement | null>
  onClickMoment?: (navigateTo: (arg: any) => void, primaryBrand: string) => void
  onContextMenu?: () => void
}

export const MomentCardBody: React.FC<MomentCardBodyProps> = ({
  className,
  closeRenamePopover,
  contextMenuCallbacks,
  isRenamePopoverOpen,
  isSelectable,
  isSelected,
  moment,
  momentCardRef,
  onClickMoment,
  onContextMenu,
}) => {
  const {
    collapseContent,
    coverImageDisplay,
    placeholderColor,
    workflowOnMomentsEnabled,
  } = useWeekViewSettings()

  const { navigateTo } = useContext(CardContext) ?? {}

  const isPlaceholder = React.useMemo(
    () => !moment || moment.postCount === 0 || moment.postCount === undefined,
    [moment]
  )
  const cardClass = isPlaceholder ? 'emptyMomentCard' : 'momentCard'

  const onClick = (navigateTo: (arg: any) => void, primaryBrand: string) => {
    onClickMoment?.(navigateTo, primaryBrand)
  }

  const backgroundColor = useMemo(
    () =>
      lookupPlaceholderHex(
        isPlaceholder ? placeholderColor : PlaceholderColor.white
      ),
    [placeholderColor, isPlaceholder]
  )

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <motion.div
      animate={{ backgroundColor }}
      initial={{ backgroundColor }}
      transition={{
        type: 'spring',
        damping: 80,
        mass: 3,
        stiffness: 100,
      }}
      className={kuiperClassNames(
        cardClass,
        `${cardClass}-${coverImageDisplay}`,
        {
          [`${cardClass}-isCustom`]:
            coverImageDisplay !== CoverImageDisplayMode.Standard,
        },
        { [`${cardClass}-isExpanded`]: !collapseContent },
        { [`${cardClass}-isSelectable`]: isSelectable },
        { [`${cardClass}-showDate`]: Boolean(moment.scheduled_end_at) },
        { [`${cardClass}-hasPreview`]: Boolean(moment.preview) },
        { [`${cardClass}--removed`]: moment.isRemoved },
        { [`${cardClass}--selected`]: isSelected },
        { [`${cardClass}--shared`]: moment.primaryBrand },
        { [`${cardClass}--isSaving`]: moment.isSaving },
        className
      )}
      onContextMenu={onContextMenu}
      ref={momentCardRef}
    >
      <div
        onClick={
          navigateTo
            ? () => {
                onClick(navigateTo, moment.primaryBrand)
              }
            : undefined
        }
      >
        {Boolean(
          moment.preview && coverImageDisplay !== CoverImageDisplayMode.NoImage
        ) && <MomentCardImage moment={moment} />}
        {isRenamePopoverOpen && (
          <RenamePopover
            entity={moment}
            isOpen={isRenamePopoverOpen}
            maxLength={60}
            closePopover={closeRenamePopover}
            label="Rename Moment"
            renameEntity={(id: string, momentName: string) => {
              contextMenuCallbacks?.renameMoment?.(id, momentName)
            }}
            trigger={<HeadingAndDetails moment={moment} />}
          />
        )}
        {!isRenamePopoverOpen && <HeadingAndDetails moment={moment} />}
        {workflowOnMomentsEnabled ? (
          <ConnectedMomentWorkflowStatus moment={moment} />
        ) : null}
      </div>
    </motion.div>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}
